import React, { useContext, useEffect, useRef } from "react";
import GlobalConfig from "../../context/GlobalConfig";
import JourneyConfig from "../../context/JourneyConfig";
import { languageData } from "../../constants/languageData";
import { useNavigate, useSearchParams } from 'react-router-dom';
import Footer from "../../components/Footer";
import Head from "../Head";
import RenderSvg from "../../components/RenderSvg";
import "./analyzing.css";
import { useParams } from "react-router-dom";
import { findNextRoute } from "../../utils/orchestrationModule";
import { getDocumentFieldName } from "../../utils/documentFields";
import { countries } from "../../constants/countries";

// True when the document accepts this country with at least one document type to capture.
function isCountryOffered(documentCapture, documentIndex, countryCode) {
  const countryName = countries.find((country) => country.value === countryCode)?.label;
  const offered = documentCapture?.[documentIndex]?.countries?.find(
    (eachCountry) => eachCountry.country === countryName
  );
  return Boolean(offered?.documentsAllowed?.length || offered?.noDoc?.length);
}

function Analyzing({ stepperFunc, setErrorType, steps, setJourney, isFirstDocumentVerificationDone, updateDocumentStep, setFirstVerifiedDocumentName, navigation, stepObj, currentDocumentStep, currentStep, currentDocumentRetries , frontImageRef, backImageRef}) {
  const navigate = useNavigate();
  const { journeyId: journeyIdFromParams } = useParams();
  const jId = sessionStorage.getItem("jId") ?? journeyIdFromParams;
  const { config } = useContext(GlobalConfig);
  const { journey, updateJourney, getJourney } = useContext(JourneyConfig);
  const language = sessionStorage.getItem("language") ?? "en";
  const { branding } = config ?? {};
  const queryParams = useSearchParams()[0];
  const source = queryParams.get("source");
  const hasUpdatedRef = useRef(false);

  useEffect(() => {
    if (journey && Object.keys(navigation).length && Object.keys(stepObj).length && stepObj?.[currentStep]?.includes("analysis")) {
      handleUpdateJourney();
    }
  }, [journey, navigation, currentStep]);

  const handleUpdateJourney = async () => {
    if (hasUpdatedRef.current) return;
    hasUpdatedRef.current = true;
    if (sessionStorage.getItem('isFlipScreenShown')) sessionStorage.removeItem('isFlipScreenShown');
    const failureReason = sessionStorage.getItem("documentFailureReason");
    const result = await updateJourney(
      "DOC_CHECK",
      {
        currentDocumentStep: currentDocumentStep+1,
        currentDocumentRetries: currentDocumentRetries,
        failureReason: failureReason || null, 
      },
      currentStep
    );
    if (frontImageRef) frontImageRef.current = null;
    if (backImageRef) backImageRef.current = null;
    if (!result.error) {
      // Dedup TERMINATE: the document verified, but the journey matched an existing one and the
      // backend terminated it. This returns isJourneyDone (no moveJourneyAhead), so handle it BEFORE
      // the moveJourneyAhead / doc-rejection branches — otherwise it would fall through to the retry
      // path. Store the backend message so the terminate page shows it, then route there.
      if (result?.isJourneyDone && result?.journeyDoneReason === "DUPLICATE_FOUND") {
        if (result?.message) {
          sessionStorage.setItem("terminationMessage", result.message);
        }
        navigate(`/${journeyIdFromParams}/verification-terminated`);
        return;
      }
      if (result?.moveJourneyAhead) {
        sessionStorage.removeItem("documentFailureReason");
        // When the NEXT document was prefilled by the client, its country and selection screens are
        // both skipped and the applicant goes straight to capturing it.
        //
        // This was written for document 2 only (idType2 / documentCapture[1], gated on
        // "!isFirstDocumentVerificationDone" — i.e. "we just finished document 1"). currentDocumentStep
        // carries the same signal generically: it is still the 0-based index of the document that was
        // just verified, because updateDocumentStep's state change has not landed yet. So document 3's
        // prefill, which used to be stranded, now takes the identical path.
        //
        // Which country counts as prefilled depends on whom the journey asks. A country screen of
        // this document's own is the only thing that makes `country` document 1's answer rather
        // than the journey's, so that is what decides it: when the question is put once up front
        // every document shares the one answer and `country` skips the screen exactly as it always
        // did; when each document is asked separately, only its own `country{N}` will do. Skipping
        // on `country` there stranded the document with no country of its own, and
        // getDocumentCountry then fell back to document 1's — capturing against a country that
        // document's configuration may well exclude. A prefill may skip a screen; it may never
        // guess a country.
        const nextDocumentIndex = currentDocumentStep + 1;
        const nextIdTypeKey = getDocumentFieldName('idType', nextDocumentIndex);
        const idDoc2Prefilled = journey?.userInfo?.id?.[nextIdTypeKey];
        const asksThisDocumentItsOwnCountry = Boolean(stepObj?.[steps?.[0]]?.includes("country"));
        const countryPrefilled = asksThisDocumentItsOwnCountry
          ? journey?.userInfo?.id?.[getDocumentFieldName('country', nextDocumentIndex)]
          : journey?.userInfo?.id?.country;
        const isSecondDocumentVerificationConfigured = config?.documentCapture?.[nextDocumentIndex]?.required;
        if (result?.firstVerifiedDocumentName) {
          setFirstVerifiedDocumentName(result?.firstVerifiedDocumentName);
          sessionStorage.setItem("firstVerifiedDocumentName", result?.firstVerifiedDocumentName);
        }
        // Every document verified so far, not just the first — the document selection screen needs
        // all of them so it never offers a document the applicant has already submitted. Persisted
        // the same way firstVerifiedDocumentName is, so it survives a refresh mid-journey.
        if (Array.isArray(result?.verifiedDocumentNames)) {
          sessionStorage.setItem(
            "verifiedDocumentNames",
            JSON.stringify(result.verifiedDocumentNames)
          );
        }
        if (Array.isArray(result?.verifiedDocuments)) {
          sessionStorage.setItem("verifiedDocuments", JSON.stringify(result.verifiedDocuments));
        }
        setErrorType("");
        updateDocumentStep(true);
        if (idDoc2Prefilled && countryPrefilled && isSecondDocumentVerificationConfigured) {
          const cameraAccessAllowed = sessionStorage.getItem('cameraAccessAllowed');
          sessionStorage.removeItem("document");
          sessionStorage.setItem('document', idDoc2Prefilled);
          setJourney({
            ...journey,
            capturedData: {
              ...journey.capturedData,
              [nextIdTypeKey]: idDoc2Prefilled,
            },
          });
          if(idDoc2Prefilled==="Digilocker" || idDoc2Prefilled==="Digilocker 2.0")
          {
              const digilockerUrl = sessionStorage.getItem("digilockerUrl");
              if (digilockerUrl) {
                  window.location.replace(digilockerUrl);
              }
          }
          else{
            // Skipping the selection screen is counted from the selection screen itself, not from
            // whatever step follows analysis. They are the same step in a journey that asks for
            // the country once up front; with a country screen per document (IDX-677) the country
            // question comes first, and counting from it lands one screen short of the camera.
            const selectionRoute = findNextRoute(stepObj, steps?.[0], "documents");
            const selectionStep = selectionRoute ? selectionRoute[0] : steps?.[0];
            stepperFunc(Number(selectionStep) + (cameraAccessAllowed ? 2 : 1));
          }
        } else if (
          asksThisDocumentItsOwnCountry &&
          isSecondDocumentVerificationConfigured &&
          isCountryOffered(config?.documentCapture, nextDocumentIndex, countryPrefilled)
        ) {
          // Country alone is prefilled: skip only the country screen, as Intro does for document 1.
          const selectionRoute = findNextRoute(stepObj, steps?.[0], "documents");
          stepperFunc(selectionRoute ? Number(selectionRoute[0]) : steps?.[0]);
        } else stepperFunc(steps?.[0]);
      } else if (config?.digilocker?.required && result?.isJourneyDone) {
        // IDX-707: backend-authoritative terminate. When the BE ends the journey it returns
        // isJourneyDone. Terminate the applicant HERE only for the DigiLocker Phase-1 terminal
        // reason (RETRIES_EXCEEDED). JOURNEY_EXPIRED / SESSION_TIMED_OUT are already routed by
        // Settings.updateJourney to their own screens (the shell renders Expired over any route),
        // so stacking a verification-terminated navigation on top of them is wrong — mirror the
        // DUPLICATE_FOUND guard above and return without navigating. (IDX-707 #3)
        //
        // P0-2: name the actual cause. The bundle returns missingDocuments (the document types
        // absent or unusable in the locker); surface them so the applicant is told what to fix.
        if (result?.journeyDoneReason === "RETRIES_EXCEEDED") {
          if (Array.isArray(result?.missingDocuments) && result.missingDocuments.length) {
            sessionStorage.setItem(
              "terminationMessage",
              `We couldn't verify the following from your DigiLocker: ${result.missingDocuments.join(", ")}. Please make sure they are added to your DigiLocker account and try again.`
            );
          } else if (result?.message) {
            sessionStorage.setItem("terminationMessage", result.message);
          }
          navigate(`/${journeyIdFromParams}/verification-terminated`);
        }
        return;
      } else if (config?.digilocker?.required && result?.digilockerUrl) {
        // IDX-707: Digilocker retry. The BE owns the Digilocker retry ceiling and issued a fresh
        // session; show the retry page WITHOUT the client-side documentCapture gate, so the FE retries
        // in lockstep with the persisted journey and never terminates early/late on its own.
        // IDX-707 #2: the retry return carries no failureReason, so default to DIGILOCKER_FAILURE —
        // otherwise sessionStorage stores the literal string "undefined" (truthy) and the retry
        // screen shows generic copy while the analytics funnel records "undefined".
        const digilockerFailureReason = result?.failureReason || "DIGILOCKER_FAILURE";
        sessionStorage.setItem("digilockerUrl", result.digilockerUrl);
        sessionStorage.setItem("documentFailureReason", digilockerFailureReason);
        setErrorType(digilockerFailureReason);
        stepperFunc(steps?.[1]);
      } else {
        // Manual document path (incl. the pre-IDX-707 Aadhaar-only Digilocker handler,
        // handleDigilockerFailure, which also returns digilockerUrl) — unchanged: the client-side
        // documentCapture retry gate decides. Route-gating above keeps this path exactly pre-707.
        // Pre-IDX-707: the legacy Aadhaar-only Digilocker handler (handleDigilockerFailure) returns a
        // digilockerUrl here too; store it so its retry can redirect (unchanged legacy behaviour).
        if (result?.digilockerUrl) {
          sessionStorage.setItem("digilockerUrl", result.digilockerUrl);
        }
        sessionStorage.setItem("documentFailureReason", result?.failureReason);
        setErrorType(result?.failureReason);
        if (updateDocumentStep(false)) {
          stepperFunc(steps?.[1]);
        } else {
          navigate(`/${journeyIdFromParams}/verification-terminated`);
        }
      }
    } else {
      console.error("Error Occured");
      stepperFunc(sessionStorage.getItem('currentStep') ? parseInt(sessionStorage.getItem('currentStep')) : 0)
    }
  };



  return (
    <>
      <Head
        style={{ zIndex: "100" }}
        stepperFunc={stepperFunc}
        showLanguages={false}
        showBackIcon
        showHelp={false}
        showMobileFlow={false}
      />
      <div className="parent-container">
        <div className="main">
          <div className="left-section">
            <RenderSvg
              config={config}
              id={"analusing"}
              svgType={"analysing"}
              className="w-[180px] h-[180px] md:w-[200px] md:h-[200px] md:relative fixed"
              style={{ marginTop: "3rem" }}
            />
          </div>

          <div
            className="right-section md:items-start items-center"
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: 24,
              fontFamily: "Inter, sans-serif",

            }}
          >
            <div className="flex flex-col">
              <div className="text-[18px] md:text-[28px] flex items-baseline font-semibold text-[#000000] md:justify-start justify-center md:text-left mt-4 md:mt-0">
                {languageData[language]?.analyzing?.heading ??
                  languageData["en"]?.analyzing?.heading}
                <div class="dot-loader ml-2"></div>
              </div>
              <div className="md:mt-4 md:mb-4 mt-6 mb-6 text-[12px] md:text-[16px] font-semibold text-[#656565] text-center md:text-left">
                {languageData[language]?.analyzing?.subHeading ??
                  languageData["en"]?.analyzing?.heading}
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>
    </>
  );
}

export default Analyzing;
